/**
 * Elaine Collection Page · Mighty Gem v1.0
 * Plan: ~/.claude/plans/vamos-a-hacer-un-mighty-gem.md
 *
 * Features:
 * - Wishlist heart con localStorage (sin auth · upgrade futuro)
 * - Add-to-cart icon con fetch /cart/add.js
 * - Gallery scroll arrows con cycle entre imágenes
 * - Mobile drawer toggle
 *
 * Anti-plasta: SVG inline · animaciones 200-400ms · cero plasta visual
 */

(function () {
  'use strict';

  const WISHLIST_KEY = 'elaine_wishlist';
  const STATE_TIMEOUT_MS = 1500;

  // ============================================================
  // 1 · Wishlist (localStorage)
  // ============================================================
  const Wishlist = {
    get() {
      try {
        return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
      } catch {
        return [];
      }
    },
    set(arr) {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(arr));
      document.dispatchEvent(new CustomEvent('elaine:wishlist:change', { detail: { count: arr.length } }));
    },
    has(productId) {
      return this.get().includes(String(productId));
    },
    toggle(productId) {
      const id = String(productId);
      const arr = this.get();
      const idx = arr.indexOf(id);
      if (idx === -1) {
        arr.push(id);
      } else {
        arr.splice(idx, 1);
      }
      this.set(arr);
      return idx === -1; // true if added
    },
    syncUI() {
      document.querySelectorAll('.elaine-wishlist-btn[data-product-id]').forEach((btn) => {
        const pid = btn.dataset.productId;
        const isSaved = this.has(pid);
        btn.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
        const heartOutline = btn.querySelector('.elaine-icon--heart:not(.elaine-icon--filled)');
        const heartFill = btn.querySelector('.elaine-icon--filled');
        if (heartOutline) heartOutline.style.display = isSaved ? 'none' : '';
        if (heartFill) heartFill.style.display = isSaved ? '' : 'none';
      });
    },
  };

  function initWishlist() {
    Wishlist.syncUI();
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.elaine-wishlist-btn');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      const pid = btn.dataset.productId;
      if (!pid) return;
      const added = Wishlist.toggle(pid);
      Wishlist.syncUI();
      // Optional: show micro-toast
      announce(added ? 'Guardado en favoritos' : 'Eliminado de favoritos');
    });
  }

  // ============================================================
  // 2 · Add-to-cart icon (fetch /cart/add.js)
  // ============================================================
  function initCartIcon() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('.elaine-cart-btn');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();

      const variantId = btn.dataset.variantId;
      // (29-abr T7 · JP feedback) UX consistency: TODAS las cards add-to-cart directamente
      // Antes: hasMultipleVariants → redirect PDP. Ahora: add primer variant disponible.
      // Si user quiere variant específica, abre PDP desde click en card body (link product).

      if (!variantId) return;

      btn.dataset.state = 'loading';
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        });
        if (!response.ok) throw new Error('Add to cart failed');
        const data = await response.json();
        btn.dataset.state = 'success';
        announce('Agregado al carrito');

        // Horizon cart drawer integration v3 (Sesion 29-abr T3 · JP feedback drawer vacio + 4s delay)
        // Root cause: Horizon escucha 'cart:update' (ThemeEvents.cartUpdate) con shape { resource, sourceId, data }
        // Fix: fetch /cart.js fresh, dispatch event con shape correcto, drawer.open() inmediato
        try {
          const cartResp = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
          if (cartResp.ok) {
            const cartData = await cartResp.json();

            // Dispatch event con shape EXACTO Horizon espera (CartUpdateEvent · ThemeEvents.cartUpdate)
            document.dispatchEvent(new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: cartData,
                sourceId: 'elaine-cart-btn',
                data: data
              }
            }));

            // Open drawer inmediato · Horizon ya refrescó contenido via cart:update
            const drawer = document.querySelector('cart-drawer-component');
            if (drawer && typeof drawer.open === 'function') drawer.open();
          }
        } catch (e) {
          // Fallback · open drawer aunque cart fetch falle
          const drawer = document.querySelector('cart-drawer-component');
          if (drawer && typeof drawer.open === 'function') drawer.open();
        }

        // Reset state after animation
        setTimeout(() => {
          btn.dataset.state = '';
        }, STATE_TIMEOUT_MS);
      } catch (err) {
        console.error('[elaine-cart]', err);
        btn.dataset.state = 'error';
        announce('Error al agregar · ve al producto', 'assertive');
        setTimeout(() => {
          btn.dataset.state = '';
        }, STATE_TIMEOUT_MS);
      }
    });
  }

  // ============================================================
  // 3 · Gallery scroll arrows · interactua con <slideshow-component> nativo Horizon
  //     Métodos: .next() · .previous() · .select(idx)
  // ============================================================
  function initGalleryArrows() {
    document.addEventListener('click', (e) => {
      const arrow = e.target.closest('.elaine-gallery-arrow');
      if (!arrow) return;
      e.preventDefault();
      e.stopPropagation();

      const card = arrow.closest('product-card, .product-card');
      if (!card) return;

      const slideshow = card.querySelector('slideshow-component');
      if (!slideshow) return;

      const isNext = arrow.classList.contains('elaine-gallery-arrow--next');
      try {
        if (isNext && typeof slideshow.next === 'function') {
          slideshow.next();
        } else if (!isNext && typeof slideshow.previous === 'function') {
          slideshow.previous();
        }
      } catch (err) {
        console.warn('[elaine-gallery]', err);
      }

      // Sync dots indicator (después de un frame para que Horizon update internal state)
      requestAnimationFrame(() => {
        const slides = slideshow.querySelectorAll('slideshow-slide');
        const visibleIdx = Array.from(slides).findIndex(s => s.getAttribute('aria-hidden') === 'false');
        const dots = card.querySelectorAll('.elaine-gallery-dot');
        dots.forEach((d, i) => d.classList.toggle('elaine-gallery-dot--active', i === visibleIdx));
      });
    });
  }

  // ============================================================
  // 4 · A11y · screen reader announcements
  // ============================================================
  let announcer;
  function announce(message, priority = 'polite') {
    if (!announcer) {
      announcer = document.createElement('div');
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      announcer.style.cssText =
        'position:absolute;left:-9999px;width:1px;height:1px;overflow:hidden';
      document.body.appendChild(announcer);
    }
    announcer.setAttribute('aria-live', priority);
    announcer.textContent = '';
    setTimeout(() => {
      announcer.textContent = message;
    }, 50);
  }

  // ============================================================
  // 4.5 · Fix $ encimado en filter de precio · CSS no gana
  //       Hide label + prepend $ al placeholder via JS (always wins)
  // ============================================================
  function fixPriceLabels() {
    document.querySelectorAll('.price-facet__label, .field__label.price-facet__label').forEach(label => {
      label.style.display = 'none';
    });
    document.querySelectorAll('.price-facet__input, input[name^="filter.v.price"]').forEach(inp => {
      if (!inp.dataset.elaineDollarized) {
        const ph = inp.placeholder || '';
        if (!ph.startsWith('$')) {
          inp.placeholder = '$ ' + ph;
        }
        inp.style.setProperty('padding-left', '10px', 'important');
        inp.style.setProperty('text-align', 'left', 'important');
        inp.dataset.elaineDollarized = '1';
      }
    });
  }

  // ============================================================
  // 5 · Reorganize layout · sidebar PURE filters · top toolbar para count/sort/density
  //     SOTA e-commerce: filters izq · sort top-right · count top-left
  // ============================================================
  function reorganizeLayout() {
    const wrapper = document.querySelector('.collection-wrapper');
    if (!wrapper) return;

    const horizontal = wrapper.querySelector(':scope > .facets--horizontal, :scope > .facets-controls-wrapper');
    const grid = wrapper.querySelector(':scope > #ResultsList, :scope > .main-collection-grid, :scope > div.grid.main-collection-grid');

    // Guard por PRESENCIA (no por bandera): el morph AJAX de facets reemplaza
    // el DOM pero conserva el wrapper, así que una bandera en dataset se queda
    // pegada y el toolbar quedaba huérfano tras cada filtro (bug cazado por
    // el eval gate F2 · 1-jul-2026). Si el toolbar sigue completo, solo
    // barremos cualquier barra horizontal re-renderizada y salimos.
    const aliveToolbar = grid && grid.querySelector(':scope > .elaine-grid-toolbar .elaine-toolbar-sort');
    if (aliveToolbar) {
      if (horizontal) horizontal.remove();
      return;
    }

    if (!horizontal || !grid) return;

    // Create top toolbar inside grid (above products)
    let toolbar = grid.querySelector(':scope > .elaine-grid-toolbar');
    if (!toolbar) {
      toolbar = document.createElement('div');
      toolbar.className = 'elaine-grid-toolbar';
      grid.insertBefore(toolbar, grid.firstChild);
    }

    // Extract count + sort + density del horizontal · move to toolbar
    const count = horizontal.querySelector('.products-count-wrapper');
    const sort = horizontal.querySelector('sorting-filter-component, .sorting-filter');
    const density = horizontal.querySelector('[class*="grid-density"], .facets__grid-density-buttons, .grid-density-buttons');

    if (count) {
      count.classList.add('elaine-toolbar-count');
      toolbar.appendChild(count);
    }
    if (density) {
      density.classList.add('elaine-toolbar-density');
      toolbar.appendChild(density);
    }
    if (sort) {
      sort.classList.add('elaine-toolbar-sort');
      toolbar.appendChild(sort);
    }

    // REMOVE remaining horizontal completely (was emptied · ocupaba space en sidebar)
    horizontal.remove();
    wrapper.classList.add('elaine-sidebar-unified');

    // Página X de Y fusionado en la línea del conteo (una sola barra ·
    // eval gate F2: la barra separada "Mostrando..." duplicaba el conteo)
    injectPageStatusInline(grid, toolbar);
  }

  // ============================================================
  // 5b · Page status INLINE · "34 artículos · Página 1 de 2" (una línea)
  //      Reemplaza a injectPageStatus (barra separada · deprecada 1-jul-2026)
  // ============================================================
  function injectPageStatusInline(grid, toolbar) {
    if (!grid || !toolbar) return;
    // matar cualquier barra separada previa
    grid.querySelectorAll(':scope > .elaine-page-status').forEach((n) => n.remove());

    const productGridEl = grid.querySelector('[data-last-page]');
    const totalPages = productGridEl ? parseInt(productGridEl.dataset.lastPage, 10) : 1;
    const urlParams = new URLSearchParams(window.location.search);
    const currentPage = parseInt(urlParams.get('page') || '1', 10);

    const host = toolbar.querySelector('.elaine-toolbar-count');
    if (!host) return;
    let statusSpan = host.querySelector('.elaine-page-status-inline');
    if (!statusSpan) {
      statusSpan = document.createElement('span');
      statusSpan.className = 'elaine-page-status-inline';
      host.appendChild(statusSpan);
    }
    statusSpan.textContent = totalPages > 1 ? ' · Página ' + currentPage + ' de ' + totalPages : '';
  }

  // ============================================================
  // 5b · Pagination status indicator · "Mostrando X-Y de N · Página X de Y"
  //      ARRIBA del toolbar Ordenar (request JP · sesión 28-abr)
  //      Reads data from server-rendered DOM attrs (data-last-page, ?page=N)
  //      Uses safe DOM API (no innerHTML) to prevent XSS
  // ============================================================
  function injectPageStatus(grid, toolbar) {
    if (!grid || !toolbar) return;

    let pageStatus = grid.querySelector(':scope > .elaine-page-status');
    if (!pageStatus) {
      pageStatus = document.createElement('div');
      pageStatus.className = 'elaine-page-status';
      pageStatus.setAttribute('role', 'status');
      pageStatus.setAttribute('aria-live', 'polite');
      grid.insertBefore(pageStatus, toolbar);
    }

    // Read paginate data from server-rendered DOM
    const productGridEl = grid.querySelector('[data-last-page]');
    const totalPages = productGridEl ? parseInt(productGridEl.dataset.lastPage, 10) : 1;

    // Current page from URL ?page=N (default 1)
    const urlParams = new URLSearchParams(window.location.search);
    const currentPage = parseInt(urlParams.get('page') || '1', 10);

    // Total items from products-count text "68 artículos"
    const countEl = toolbar.querySelector('.elaine-toolbar-count [role="status"], .elaine-toolbar-count');
    const countText = countEl ? countEl.textContent.trim() : '0';
    const totalMatch = countText.match(/(\d+)/);
    const totalItems = totalMatch ? parseInt(totalMatch[1], 10) : 0;

    // Page size = 20 (matches collection.json products_per_page setting)
    const pageSize = 20;
    const firstItem = (currentPage - 1) * pageSize + 1;
    const lastItem = Math.min(currentPage * pageSize, totalItems);

    // Clear previous content safely
    while (pageStatus.firstChild) pageStatus.removeChild(pageStatus.firstChild);

    if (totalPages > 1 && totalItems > 0) {
      // Build items span: "Mostrando 1–20 de 68"
      const itemsSpan = document.createElement('span');
      itemsSpan.className = 'elaine-page-status__items';
      itemsSpan.appendChild(document.createTextNode('Mostrando '));
      const itemsRangeStrong = document.createElement('strong');
      itemsRangeStrong.textContent = firstItem + '–' + lastItem;
      itemsSpan.appendChild(itemsRangeStrong);
      itemsSpan.appendChild(document.createTextNode(' de '));
      const totalStrong = document.createElement('strong');
      totalStrong.textContent = String(totalItems);
      itemsSpan.appendChild(totalStrong);

      // Divider
      const divider = document.createElement('span');
      divider.className = 'elaine-page-status__divider';
      divider.setAttribute('aria-hidden', 'true');
      divider.textContent = '·';

      // Build page nav group: [<] Página 1 de 4 [>]
      const pageGroup = document.createElement('span');
      pageGroup.className = 'elaine-page-status__page-group';

      // Prev button (disabled if currentPage = 1)
      const prevBtn = document.createElement('a');
      prevBtn.className = 'elaine-page-status__nav elaine-page-status__nav--prev';
      prevBtn.setAttribute('aria-label', 'Página anterior');
      prevBtn.textContent = '‹';
      if (currentPage > 1) {
        const prevUrl = new URL(window.location.href);
        if (currentPage - 1 === 1) prevUrl.searchParams.delete('page');
        else prevUrl.searchParams.set('page', String(currentPage - 1));
        prevBtn.href = prevUrl.pathname + (prevUrl.search || '');
      } else {
        prevBtn.classList.add('elaine-page-status__nav--disabled');
        prevBtn.setAttribute('aria-disabled', 'true');
        prevBtn.removeAttribute('href');
      }

      // Página label inline
      const pageSpan = document.createElement('span');
      pageSpan.className = 'elaine-page-status__page';
      pageSpan.appendChild(document.createTextNode('Página '));
      const currentStrong = document.createElement('strong');
      currentStrong.textContent = String(currentPage);
      pageSpan.appendChild(currentStrong);
      pageSpan.appendChild(document.createTextNode(' de ' + totalPages));

      // Next button (disabled if currentPage = totalPages)
      const nextBtn = document.createElement('a');
      nextBtn.className = 'elaine-page-status__nav elaine-page-status__nav--next';
      nextBtn.setAttribute('aria-label', 'Página siguiente');
      nextBtn.textContent = '›';
      if (currentPage < totalPages) {
        const nextUrl = new URL(window.location.href);
        nextUrl.searchParams.set('page', String(currentPage + 1));
        nextBtn.href = nextUrl.pathname + (nextUrl.search || '');
      } else {
        nextBtn.classList.add('elaine-page-status__nav--disabled');
        nextBtn.setAttribute('aria-disabled', 'true');
        nextBtn.removeAttribute('href');
      }

      pageGroup.appendChild(prevBtn);
      pageGroup.appendChild(pageSpan);
      pageGroup.appendChild(nextBtn);

      pageStatus.appendChild(itemsSpan);
      pageStatus.appendChild(divider);
      pageStatus.appendChild(pageGroup);
      pageStatus.style.display = '';
    } else {
      pageStatus.style.display = 'none';
    }
  }

  // ============================================================
  // 5c · Collapse filter accordions by default · preserve panels with active filters
  //      Canon SOTA luxury jewelry (Mejuri/Vrai/Gorjana pattern)
  //      Less cognitive load · intent-driven UX · keep context for active filters
  // ============================================================
  function collapseFiltersByDefault() {
    document.querySelectorAll('details.facets__panel[open]').forEach((panel) => {
      // Preserve panel open if user has active filter selection inside
      const hasActiveFilter = panel.querySelector(
        'input[type="checkbox"]:checked, input[type="radio"]:checked, [aria-checked="true"], [data-selected="true"]'
      );
      // Also preserve if URL has filter param matching this panel's facet name
      let hasUrlFilter = false;
      const facetInputs = panel.querySelectorAll('input[name^="filter."]');
      const urlParams = new URLSearchParams(window.location.search);
      facetInputs.forEach((input) => {
        const paramName = input.getAttribute('name');
        if (paramName && urlParams.has(paramName)) {
          hasUrlFilter = true;
        }
      });
      if (!hasActiveFilter && !hasUrlFilter) {
        panel.removeAttribute('open');
      }
    });
  }

  // Backwards compat alias
  function unifySidebarSticky() { reorganizeLayout(); }

  // ============================================================
  // 7 · Breadcrumb cleanup · cortar SKU largo del ultimo item (Sesion 29-abr T2)
  //     "[EN-AN-CI-DL-OB-01] Elaine Anillo Cieli 0.97 cts OB 14K"
  //   → "Anillo Cieli 0.97 cts OB 14K" (sin SKU bracket · sin redundante "Elaine")
  // ============================================================
  function cleanupBreadcrumb() {
    const breadcrumb = document.querySelector('.section--breadcrumbs nav, [class*="breadcrumbs"] nav');
    if (!breadcrumb) return;
    const current = breadcrumb.querySelector('[aria-current="page"]');
    if (!current || current.dataset.elaineRewritten === '1') return;
    const text = current.textContent.trim();
    let cleaned = text.replace(/^\s*\[[^\]]+\]\s*/, '');
    cleaned = cleaned.replace(/^Elaine\s+/, '');
    current.textContent = cleaned;
    current.dataset.elaineRewritten = '1';
  }

  // ============================================================
  // 8 · PDP Back Button · floating bottom-left con sessionStorage (Sesion 29-abr T2)
  //     Si JP viene de /collections/* o /search → boton "← Volver" lleva atras
  //     Sino fallback a /collections/all (Joyeria home)
  // ============================================================
  const REFERRER_KEY = 'elaine_pdp_referrer';
  const SCROLL_KEY = 'elaine_pdp_scroll';

  function captureReferrer() {
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href*="/products/"]');
      if (!link) return;
      try {
        sessionStorage.setItem(REFERRER_KEY, JSON.stringify({
          url: window.location.pathname + window.location.search,
          title: document.title,
          timestamp: Date.now()
        }));
        sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
      } catch (err) {}
    }, true);
  }

  // SVG-safe DOM builder · createElementNS para SVG (anti-XSS)
  const SVG_NS = 'http://www.w3.org/2000/svg';
  function svg(attrs, children) {
    const el = document.createElementNS(SVG_NS, attrs.tag || 'svg');
    Object.entries(attrs).forEach(([k, v]) => { if (k !== 'tag') el.setAttribute(k, v); });
    (children || []).forEach((c) => el.appendChild(c));
    return el;
  }

  function injectBackButton() {
    if (!window.location.pathname.match(/\/products\//)) return;
    if (document.querySelector('.elaine-pdp-back')) return;

    let referrerData = null;
    try {
      const raw = sessionStorage.getItem(REFERRER_KEY);
      if (raw) referrerData = JSON.parse(raw);
    } catch (err) {}

    let label = 'Joyería';
    let url = '/collections/all';
    if (referrerData && referrerData.url) {
      url = referrerData.url;
      if (url.includes('/collections/all')) label = 'Joyería';
      else if (url.includes('/collections/')) {
        const slug = url.match(/\/collections\/([^/?]+)/)?.[1];
        label = slug ? slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, ' ') : 'Colección';
      } else if (url.includes('/search')) label = 'Resultados';
      else if (url === '/' || url === '') label = 'Inicio';
    }

    const btn = document.createElement('a');
    btn.className = 'elaine-pdp-back';
    btn.href = url;
    btn.setAttribute('aria-label', `Volver a ${label}`);

    const arrow = document.createElement('span');
    arrow.className = 'elaine-pdp-back__arrow';
    arrow.textContent = '←'; // ← unicode safe
    btn.appendChild(arrow);

    const txt = document.createElement('span');
    txt.textContent = label;
    btn.appendChild(txt);

    document.body.appendChild(btn);

    btn.addEventListener('click', () => {
      try {
        const scroll = sessionStorage.getItem(SCROLL_KEY);
        if (scroll) sessionStorage.setItem('elaine_restore_scroll', scroll);
      } catch (err) {}
    });

    requestAnimationFrame(() => {
      setTimeout(() => btn.setAttribute('data-visible', 'true'), 100);
    });
  }

  function restoreScrollOnCollection() {
    if (!window.location.pathname.match(/\/collections\//)) return;
    let scroll = null;
    try {
      scroll = sessionStorage.getItem('elaine_restore_scroll');
      if (scroll) sessionStorage.removeItem('elaine_restore_scroll');
    } catch (err) {}
    if (scroll) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: parseInt(scroll, 10), behavior: 'auto' });
      });
    }
  }

  // ============================================================
  // 9 · Wishlist Drawer · replica del cart drawer · slide from right
  //     §29-abr-2026 T2 · JP request "drawer wishlist"
  // ============================================================
  let wishlistDrawer = null;
  let wishlistDrawerOverlay = null;

  function buildCloseSvg() {
    // Build SVG safely via createElementNS
    const root = document.createElementNS(SVG_NS, 'svg');
    root.setAttribute('width', '20');
    root.setAttribute('height', '20');
    root.setAttribute('viewBox', '0 0 24 24');
    root.setAttribute('fill', 'none');
    root.setAttribute('stroke', 'currentColor');
    root.setAttribute('stroke-width', '1.5');
    root.setAttribute('stroke-linecap', 'round');
    root.setAttribute('stroke-linejoin', 'round');
    root.setAttribute('aria-hidden', 'true');
    const l1 = document.createElementNS(SVG_NS, 'line');
    l1.setAttribute('x1', '18'); l1.setAttribute('y1', '6'); l1.setAttribute('x2', '6'); l1.setAttribute('y2', '18');
    const l2 = document.createElementNS(SVG_NS, 'line');
    l2.setAttribute('x1', '6'); l2.setAttribute('y1', '6'); l2.setAttribute('x2', '18'); l2.setAttribute('y2', '18');
    root.appendChild(l1);
    root.appendChild(l2);
    return root;
  }

  function buildWishlistDrawer() {
    if (document.querySelector('.elaine-wishlist-drawer')) return;

    wishlistDrawerOverlay = document.createElement('div');
    wishlistDrawerOverlay.className = 'elaine-wishlist-drawer__overlay';
    wishlistDrawerOverlay.setAttribute('aria-hidden', 'true');
    document.body.appendChild(wishlistDrawerOverlay);

    wishlistDrawer = document.createElement('aside');
    wishlistDrawer.className = 'elaine-wishlist-drawer';
    wishlistDrawer.setAttribute('role', 'dialog');
    wishlistDrawer.setAttribute('aria-modal', 'true');
    wishlistDrawer.setAttribute('aria-labelledby', 'elaine-wishlist-drawer-title');
    wishlistDrawer.setAttribute('data-open', 'false');

    const header = document.createElement('div');
    header.className = 'elaine-wishlist-drawer__header';

    const title = document.createElement('h2');
    title.className = 'elaine-wishlist-drawer__title';
    title.id = 'elaine-wishlist-drawer-title';
    title.textContent = 'Mis favoritos';
    header.appendChild(title);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'elaine-wishlist-drawer__close';
    closeBtn.setAttribute('aria-label', 'Cerrar lista de favoritos');
    closeBtn.appendChild(buildCloseSvg());
    closeBtn.addEventListener('click', closeWishlistDrawer);
    header.appendChild(closeBtn);

    wishlistDrawer.appendChild(header);

    const body = document.createElement('div');
    body.className = 'elaine-wishlist-drawer__body';
    wishlistDrawer.appendChild(body);

    document.body.appendChild(wishlistDrawer);

    wishlistDrawerOverlay.addEventListener('click', closeWishlistDrawer);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && wishlistDrawer.getAttribute('data-open') === 'true') closeWishlistDrawer();
    });
  }

  async function renderWishlistItems() {
    if (!wishlistDrawer) return;
    const body = wishlistDrawer.querySelector('.elaine-wishlist-drawer__body');
    if (!body) return;

    const ids = Wishlist.get();

    // Clear safely
    while (body.firstChild) body.removeChild(body.firstChild);

    if (ids.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'elaine-wishlist-drawer__empty';
      empty.textContent = 'Aun no has guardado piezas. Dale al corazon en una pieza para agregarla aqui.';
      body.appendChild(empty);
      return;
    }

    const list = document.createElement('ul');
    list.className = 'elaine-wishlist-drawer__items';
    body.appendChild(list);

    for (const productId of ids) {
      try {
        // Direct selector for product-card element (mas robusto · avoids LI parent collision)
        let card = document.querySelector(`product-card[data-product-id="${productId}"]`);
        if (!card) {
          // Fallback: search via any element con productId then descend
          const initial = document.querySelector(`[data-product-id="${productId}"]`);
          card = initial?.querySelector('product-card, .product-card') ||
                 initial?.closest('product-card, .product-card') ||
                 initial;
        }
        renderWishlistItemFromCard(list, productId, card);
      } catch (err) {}
    }
  }

  function renderWishlistItemFromCard(list, productId, card) {
    const li = document.createElement('li');
    li.className = 'elaine-wishlist-drawer__item';
    li.dataset.productId = productId;

    const cardImg = card?.querySelector('img');
    if (cardImg) {
      const img = document.createElement('img');
      img.className = 'elaine-wishlist-drawer__item-img';
      img.src = cardImg.src;
      img.alt = cardImg.alt || '';
      img.loading = 'lazy';
      li.appendChild(img);
    } else {
      const placeholder = document.createElement('div');
      placeholder.className = 'elaine-wishlist-drawer__item-img';
      li.appendChild(placeholder);
    }

    const info = document.createElement('div');
    info.className = 'elaine-wishlist-drawer__item-info';

    const cardLink = card?.querySelector('a[href*="/products/"]');
    const title = document.createElement('a');
    title.className = 'elaine-wishlist-drawer__item-title';
    title.href = cardLink?.href || '#';
    const rawTitle = cardLink?.textContent.trim() || 'Pieza guardada';
    title.textContent = rawTitle.replace(/^\[[^\]]+\]\s*/, '').replace(/^Elaine\s+/, '');
    info.appendChild(title);

    const cardPrice = card?.querySelector('[class*="price"]');
    if (cardPrice) {
      const price = document.createElement('span');
      price.className = 'elaine-wishlist-drawer__item-price';
      price.textContent = cardPrice.textContent.trim();
      info.appendChild(price);
    }

    const remove = document.createElement('button');
    remove.className = 'elaine-wishlist-drawer__item-remove';
    remove.textContent = 'Quitar';
    remove.setAttribute('aria-label', 'Quitar de favoritos');
    remove.addEventListener('click', () => {
      Wishlist.toggle(productId);
      Wishlist.syncUI();
      updateWishlistCounter();
      renderWishlistItems();
    });
    info.appendChild(remove);

    li.appendChild(info);
    list.appendChild(li);
  }

  function openWishlistDrawer() {
    if (!wishlistDrawer) buildWishlistDrawer();
    renderWishlistItems();
    wishlistDrawer.setAttribute('data-open', 'true');
    wishlistDrawerOverlay.setAttribute('data-open', 'true');
    document.body.style.overflow = 'hidden';
  }

  function closeWishlistDrawer() {
    if (!wishlistDrawer) return;
    wishlistDrawer.setAttribute('data-open', 'false');
    wishlistDrawerOverlay.setAttribute('data-open', 'false');
    document.body.style.overflow = '';
  }

  function updateWishlistCounter() {
    const count = Wishlist.get().length;
    document.querySelectorAll('.elaine-wishlist-counter, [data-wishlist-counter]').forEach((el) => {
      el.textContent = count > 0 ? String(count) : '';
      el.setAttribute('data-count', String(count));
    });
  }

  function initWishlistDrawerOpener() {
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-open-wishlist]');
      if (!opener) return;
      e.preventDefault();
      openWishlistDrawer();
    });

    document.addEventListener('elaine:wishlist:change', (e) => {
      updateWishlistCounter();
      if (e.detail && e.detail.count > 0 && e.detail.added === true) {
        openWishlistDrawer();
      }
    });

    updateWishlistCounter();
  }

  // Patch Wishlist.set to dispatch enhanced event con info de added
  const _wishlistSetOriginal = Wishlist.set.bind(Wishlist);
  Wishlist.set = function (arr) {
    const prev = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    const added = arr.length > prev.length;
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(arr));
    document.dispatchEvent(new CustomEvent('elaine:wishlist:change', { detail: { count: arr.length, added } }));
  };

  // ============================================================
  // 6 · Init on DOM ready
  // ============================================================
  function init() {
    unifySidebarSticky();
    fixPriceLabels();
    initWishlist();
    initCartIcon();
    initGalleryArrows();
    collapseFiltersByDefault();
    cleanupBreadcrumb();
    captureReferrer();
    injectBackButton();
    restoreScrollOnCollection();
    initWishlistDrawerOpener();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-sync tras cada morph AJAX de facets (filtro/sort/paginación).
  // NOTA (fix gate F2 · 1-jul-2026): el evento 'results-list:rendered' que se
  // escuchaba antes NO existe en Horizon (nadie lo emite · listener muerto).
  // Observamos mutaciones del wrapper con debounce: es agnóstico a la
  // implementación interna del section-renderer del vendor.
  (function observeResultsRerender() {
    const target = document.querySelector('.collection-wrapper') || document.querySelector('#ResultsList');
    if (!target || typeof MutationObserver === 'undefined') return;
    let t = null;
    const resync = () => {
      Wishlist.syncUI();
      unifySidebarSticky();
      fixPriceLabels();
      collapseFiltersByDefault();
      updateWishlistCounter();
    };
    const observer = new MutationObserver(() => {
      if (t) clearTimeout(t);
      t = setTimeout(resync, 150);
    });
    observer.observe(target, { childList: true, subtree: true });
  })();
})();

